/**
 * AddOnBundlesPage Component
 *
 * "Learn more" page for add-on bundles with tabbed navigation.
 * Content is controlled via Statsig Dynamic Config (addon_bundles_config).
 *
 * URL: /bundles
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Grid, Paper, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useAddOnBundlesConfig, useBundleCatalog } from '../../store/hooks';
import type { AddOnBundleTab, BillingInterval } from '../../types';
import { slugifyBundleName } from '../../types';
import { BillingIntervalToggle } from '../BillingIntervalToggle';
import { TierCard } from '../TierCard';

import { BundleTabContent } from './BundleTabContent';

/**
 * Location state passed when navigating to this page
 */
export interface BundlePageLocationState {
  showAddToPlan?: boolean;
  returnPath?: string;
}

export const AddOnBundlesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const config = useAddOnBundlesConfig();
  const { subscription } = useEventTracking();
  const { tiers, addOnsForDisplay, annualDiscountRange } = useBundleCatalog();
  const showTiersSection = tiers.length > 1;
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('year'); // Default to annual

  // Helper to get tab ID (from explicit id or slugified bundleName)
  const getTabId = useCallback((tab: AddOnBundleTab): string => {
    if (tab.bundleName) {
      return slugifyBundleName(tab.bundleName);
    }
    // Fallback: slugify the label if bundleName is missing
    return slugifyBundleName(tab.label);
  }, []);

  // Store location state in useState to persist across tab changes
  // (setSearchParams clears location.state, so we capture it on mount)
  const [showAddToPlan] = useState(() => {
    const state = location.state as BundlePageLocationState | null;
    return state?.showAddToPlan ?? false;
  });
  const [returnPath] = useState(() => {
    const state = location.state as BundlePageLocationState | null;
    return state?.returnPath;
  });

  // Get current tab from URL or default to first tab
  const currentTabId = useMemo(() => {
    const tab = searchParams.get('tab');
    const validTabIds = config.tabs.map(t => getTabId(t));
    if (tab && validTabIds.includes(tab)) {
      return tab;
    }
    return config.tabs[0] ? getTabId(config.tabs[0]) : '';
  }, [searchParams, config.tabs, getTabId]);

  // Get current tab config
  const currentTab = useMemo(() => {
    return config.tabs.find(t => getTabId(t) === currentTabId) ?? config.tabs[0];
  }, [config.tabs, currentTabId, getTabId]);

  // Track page viewed on mount
  const pageViewedRef = useRef(false);
  useEffect(() => {
    if (!pageViewedRef.current) {
      pageViewedRef.current = true;
      subscription.bundlePageViewed({ current_tab: currentTabId });
    }
  }, [currentTabId, subscription]);

  // Handle tab change
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newTabId: string) => {
      subscription.bundleTabChanged({
        previous_tab: currentTabId,
        new_tab: newTabId,
      });
      setSearchParams({ tab: newTabId });
    },
    [setSearchParams, currentTabId, subscription],
  );

  // Handle "Add To Plan" button click
  const handleAddToPlanClick = useCallback(() => {
    subscription.bundleBackClicked({ from_tab: currentTabId });
    if (returnPath) {
      navigate(returnPath);
    } else {
      navigate(-1);
    }
  }, [navigate, currentTabId, subscription, returnPath]);

  // Handle Talk to Sales click (analytics only - navigation handled by native link)
  const handleTalkToSalesClick = useCallback(() => {
    if (currentTab?.calendlyUrl) {
      subscription.bundleTalkToSalesClicked({
        tab: currentTabId,
        destination_url: currentTab.calendlyUrl,
      });
    }
  }, [currentTab, currentTabId, subscription]);

  return (
    <Container sx={{ py: 4 }} maxWidth="lg">
      {/* Subscription Plans Section - only show if multiple tiers */}
      {showTiersSection && (
        <Box sx={{ mb: 6 }}>
          {/* Subscription Plans Section Header */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: 2,
              mb: 3,
            }}
          >
            {/* Left: Title and subtitle */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                Subscription Plans
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                Choose the plan that best fits your practice&apos;s needs
              </Typography>
            </Box>

            {/* Right: Billing toggle */}
            <BillingIntervalToggle
              value={billingInterval}
              onChange={setBillingInterval}
              discountRange={annualDiscountRange}
            />
          </Box>
          <Grid container spacing={3}>
            {tiers.map(tier => (
              <Grid size={{ xs: 12, md: 4 }} key={tier.bundle_id}>
                <TierCard
                  bundle={tier}
                  billingInterval={billingInterval}
                  isSelected={false}
                  hideButton
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 2,
          }}
        >
          {/* Left: Title and subtitle */}
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              {config.pageTitle}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: '700px',
              }}
            >
              {config.pageIntro}
            </Typography>
          </Box>

          {/* Right: Billing toggle (only when tiers section not shown) */}
          {!showTiersSection && (
            <BillingIntervalToggle
              value={billingInterval}
              onChange={setBillingInterval}
              discountRange={annualDiscountRange}
            />
          )}
        </Box>
      </Box>

      {/* Tabs Navigation */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'transparent',
          mb: 3,
        }}
      >
        <Tabs
          value={currentTabId}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 'auto',
              px: 3,
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
          }}
        >
          {config.tabs.map(tab => {
            const tabId = getTabId(tab);
            return <Tab key={tabId} value={tabId} label={tab.label} />;
          })}
        </Tabs>
      </Paper>

      {/* Tab Content with CTAs passed as props */}
      {currentTab && (
        <BundleTabContent
          tab={currentTab}
          billingInterval={billingInterval}
          addOns={addOnsForDisplay}
          showAddToPlan={showAddToPlan}
          onAddToPlanClick={handleAddToPlanClick}
          onTalkToSalesClick={handleTalkToSalesClick}
        />
      )}
    </Container>
  );
};

export default AddOnBundlesPage;
